import React, { useMemo, useState, useEffect, Fragment, useCallback, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Popover } from '@headlessui/react'
import { BsChevronDown } from 'react-icons/bs'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import 'react-datepicker/dist/react-datepicker.css'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import Button from '@/components/common/Button'
import countries from '@/data/country.json'
import Calendar from '@/components/common/Calendar'
import useDebounce from '@/hooks/useDebounce'
import { request } from '@/utils/axiosUtil'
import Highlighter from 'react-highlight-words'
import { IoIosClose } from 'react-icons/io'
import Loader from '@/components/common/Loader'
import Dropdown from '@/components/common/Dropdown'
import countryCodes from '@/data/countryCodes.json'
import * as yup from 'yup'
import { useRouter } from 'next/router'
import { setBookNowSeo } from '@/store/slices/headSlice'

const BookingRequestForm = ({ speakerData = [], booknow = false }) => {
  const router = useRouter()
  const { asPath } = useRouter()
  const dispatch = useDispatch()

  const defaultImageUrl =
    'https://images.ctfassets.net/p6iqoqavudao/3lLPw0ct3NWRdllJaozgZr/238aa02d0b3a99038d1969146d7916f1/placeholder.png'

  const [selectedSpeakers, setSelectedSpeakers] = useState([])
  const [searchText, setSearchText] = useState('')
  const [speakersDetails, setSpeakersDetails] = useState(['Loading...'])
  const [suggestions, setSuggestions] = useState({
    nameList: [],
  })
  const [show, setShow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSearchContainerLoading, setIsSearchContainerLoading] = useState(false)
  const [error, setError] = useState('Enter at least 2 characters to search')
  const [loggedInUserDetails, setLoggedInUserDetails] = useState(
    JSON.parse(localStorage.getItem('userDetails')) || {}
  )
  const [countryDialCode, setCountryDialCode] = useState('+1')
  const [countryCode, setCountryCode] = useState('US')

  useEffect(() => {
    if (asPath) {
      if (!/^.*speaker/.test(asPath)) dispatch(setBookNowSeo())
    }
  }, [asPath])

  useEffect(() => {
    if (router?.query && typeof window !== 'undefined' && booknow) {
      setIsLoading(true)
      setTimeout(() => {
        const user = (localStorage && JSON.parse(localStorage.getItem('userDetails'))) || {}
        if (user) setLoggedInUserDetails(user)
        setIsLoading(false)
        window.scrollTo(0, 0)
      }, 3500)
    }
  }, [])

  const onInputChange = (e) => {
    const searchString = e.target.value
    if (searchString.length >= 2) {
      setSpeakersDetails(['Loading...'])
    }
    setSearchText(e.target.value)
    validateInput(e.target.value)
  }

  const validateInput = (text, selectedSpeakers) => {
    if (!text || text.length < 2) {
      selectedSpeakers && selectedSpeakers?.length < 5
        ? setError('Enter at least 2 characters to search')
        : setError(<>&#9888; *Maximum of 5 speakers per request</>)
      return false
    }

    error && setError('')
    return true
  }

  useDebounce(
    async () => {
      if (searchText?.length >= 2) {
        setSpeakersDetails(['Loading...'])
        setIsSearchContainerLoading(true)
        setShow(true)
        const apiUrl = `/unifiedAutoPopulate?query=${encodeURIComponent(
          searchText?.trim()?.toLowerCase()
        )}&bookNow=${true}`
        const res = await request({ url: apiUrl })
        const result = res?.status == 200 ? res?.data?.data : res?.response?.data
        setSpeakersDetails(result || [])
        setIsSearchContainerLoading(false)
      }
    },
    8e2,
    [searchText]
  )

  const getSuggestions = (speakers) => {
    const result = {
      nameList: [],
      categoryList: [],
      speakerTypeList: [],
    }
    speakers.forEach((item = {}) => {
      const { contentType = null, name = null, slug = null, assets = {} } = item
      switch (contentType) {
        case 'speaker':
          result.nameList.push({
            name,
            slug,
            image_url: assets?.fields?.file?.url,
          })
          break
        default:
          break
      }
    })
    return result
  }

  useEffect(() => {
    let tempSuggestions = { nameList: [] }
    let tempShow = !searchText

    if (validateInput(searchText, selectedSpeakers)) {
      if (speakersDetails?.length > 0) {
        tempSuggestions = getSuggestions(speakersDetails)
        tempShow = !(tempSuggestions?.nameList?.length === 0)
      }
    } else if (speakersDetails.length > 0) {
      speakersDetails.length > 0
    }
    setShow(tempShow)
    setSuggestions(tempSuggestions)
  }, [searchText, speakersDetails, selectedSpeakers])

  const asterisk = useMemo(() => <span aria-hidden="true">*</span>, [])

  const schema = loggedInUserDetails?.userDetails
    ? yup
        .object()
        .shape({
          searchText:
            booknow && selectedSpeakers.length === 0 && yup.string().required('Required field'),
          budgetPerEvent: yup.string().required('Required field'),
          eventDate: yup.string().required('Required field'),
          attendees: yup
            .number()
            .typeError('You must specify a number')
            .positive('No of Attendees must be positive integer')
            .integer('No of Attendees must be positive integer')
            .required('Required field'),
          eventCountry: yup.string().required('Required field'),
          eventLocation: yup.string().required('Required field'),
          eventDetails: yup.string().required('Required field'),
        })
        .required()
    : yup
        .object()
        .shape({
          searchText:
            booknow && selectedSpeakers.length === 0 && yup.string().required('Required field'),
          firstName: yup.string().required('Required field'),
          lastName: yup.string().required('Required field'),
          mobilePhone: yup
            .string()
            .matches(/^\d{10}$/, 'Please enter a valid phone number')
            .required('Required field'),
          emailAddress: yup
            .string()
            .matches(
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              'Please enter a valid email address'
            )
            .required('Required field'),
          company: yup.string().required('Required field'),
          budgetPerEvent: yup.string().required('Required field'),
          eventDate: yup.string().required('Required field'),
          attendees: yup
            .number()
            .typeError('You must specify a number')
            .positive('No of Attendees must be positive integer')
            .integer('No of Attendees must be positive integer')
            .required('Required field'),
          eventCountry: yup.string().required('Required field'),
          eventLocation: yup.string().required('Required field'),
          eventDetails: yup.string().required('Required field'),
        })
        .required()

  const onSubmit = async (data) => {
    setShow(false)
    const body = loggedInUserDetails?.userDetails
      ? {
          firstName: loggedInUserDetails.userDetails.firstName,
          lastName: loggedInUserDetails.userDetails.lastName,
          emailAddress: loggedInUserDetails.userDetails.emailAddress,
          mobilePhone: loggedInUserDetails.userDetails.mobilePhone,
          company: loggedInUserDetails.userDetails.company,
          title: loggedInUserDetails.userDetails.title,
          speakerName:
            booknow && selectedSpeakers && selectedSpeakers.length > 0
              ? selectedSpeakers?.map((item) => {
                  return item?.name
                })
              : speakerData[0]?.name,
          speakerSlug:
            booknow && selectedSpeakers && selectedSpeakers.length > 0
              ? selectedSpeakers?.map((item) => {
                  return item?.slug
                })
              : speakerData[0]?.slug,
          bookNow: booknow && booknow ? 'true' : 'false',
        }
      : {
          speakerName:
            booknow && selectedSpeakers && selectedSpeakers.length > 0
              ? selectedSpeakers?.map((item) => {
                  return item?.name
                })
              : speakerData[0]?.name,
          speakerSlug:
            booknow && selectedSpeakers && selectedSpeakers.length > 0
              ? selectedSpeakers?.map((item) => {
                  return item?.slug
                })
              : speakerData[0]?.slug,
          bookNow: booknow && booknow ? 'true' : 'false',
          mobilePhone: countryDialCode + ' ' + data.mobilePhone,
        }

    const url = '/bookingRequest'

    const res = await request({
      url: url,
      method: 'POST',
      data: JSON.stringify({ ...data, ...body }),
    })
    if (res.status == 200) {
      setIsLoading(true)
      reset({
        firstName: '',
        lastName: '',
        mobilePhone: '',
        emailAddress: '',
        company: '',
        title: '',
        eventDetails: '',
        eventDate: '',
        eventCountry: '',
        attendees: '',
        budgetPerEvent: '',
        eventType: '',
        eventLocation: '',
      })
      setSelectedSpeakers([])
      booknow
        ? router.push('/book-now/confirmation')
        : router.push(`/speaker/${speakerData[0]?.slug}/booking-request/confirmation`)
    } else if (res?.response?.data?.statusCode == 409) {
      router.push('/409')
    }
    window.scrollTo(0, 0)
  }

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  const onSpeakerSelected = useCallback(
    (item, insert = true) => {
      const tempSpeakers = [...selectedSpeakers]

      if (insert) {
        selectedSpeakers &&
          tempSpeakers.findIndex((speaker) => speaker.slug === item.slug) === -1 &&
          tempSpeakers.push(item)
        setSelectedSpeakers(tempSpeakers)
        setShow(false)
        setSearchText('')
        return
      }

      const result =
        selectedSpeakers && selectedSpeakers?.filter((speaker) => speaker.slug !== item.slug)
      setSelectedSpeakers(result)
    },
    [selectedSpeakers]
  )
  const divRef = useRef()

  useEffect(() => {
    function handleClickOutside(event) {
      if (divRef.current && !divRef.current.contains(event.target)) {
        setShow(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [divRef])

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="px-4 mt-6 lg:w-10/12 lg:mx-auto lg:mt-10 md:w-10/12 md:mx-auto md:mt-10 md:px-14 xs:mx-auto xs:mt-10 xs:px-4">
          <h1 className="text-xl tracking-wider uppercase tagline-font lg:text-4xl md:text-3xl maxSm:text-lg md:mb-[26px] mb-4">
            {booknow ? 'Book Now' : 'Request Booking For:'}
          </h1>
          {booknow ? (
            <>
              {selectedSpeakers && selectedSpeakers.length > 0 ? (
                selectedSpeakers?.map((item, i) => (
                  <div className="grid grid-cols-1 xs:grid-cols-2 xs:gap-8" key={i}>
                    <div className="flex bg-black border-[1.5px] border-gray-500 mt-2.5">
                      <Link href={item && `/speaker/${item?.slug}`} legacyBehavior>
                        {item?.image_url !== undefined ? (
                          <Image
                            src={item && `https:${item?.image_url}?w=72&h=73&q=100&fit=thumb&f=top`}
                            alt=""
                            width={72}
                            height={73}
                            quality={100}
                          />
                        ) : (
                          <Image
                            src={`${defaultImageUrl}?w=72&h=73&q=100&fit=thumb&f=top`}
                            alt=""
                            width={72}
                            height={73}
                            quality={100}
                          />
                        )}
                      </Link>
                      <div className="w-full leading-normal md:p-4">
                        <div className="relative flex">
                          <div className="py-1 ml-2 cursor-pointer text-md maxMd:mt-4">
                            <Link href={item && `/speaker/${item?.slug}`} legacyBehavior>
                              <a>{item && item?.name}</a>
                            </Link>
                          </div>
                          <IoIosClose
                            className="absolute right-[21px] md:right-0 w-6 h-6 transform -translate-y-1/2 cursor-pointer top-1/2 mt-1.5 md:mt-0"
                            onClick={() => {
                              onSpeakerSelected(item, false)
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div></div>
                  </div>
                ))
              ) : (
                <div></div>
              )}

              <div className="grid grid-cols-1 gap-8 xs:grid-cols-2">
                <div className="mt-6" ref={divRef}>
                  <label htmlFor="search" className="relative block">
                    <input
                      className={classNames(
                        'block py-2.5 px-0 w-full border-b border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer rounded-none placeholder:text-sm',
                        selectedSpeakers && selectedSpeakers?.length >= 5
                          ? 'border-b-custom-gray-40 placeholder:text-custom-gray-40'
                          : 'border-b-custom-gray-80 placeholder:text-custom-gray-80'
                      )}
                      placeHolder="Find Speaker"
                      name="searchText"
                      value={searchText}
                      onChange={onInputChange}
                      autoFocus={true}
                      disabled={selectedSpeakers && selectedSpeakers?.length === 5 ? true : false}
                    />
                    {searchText ? (
                      <IoIosClose
                        className="absolute w-6 h-6 transform -translate-y-1/2 cursor-pointer top-1/2 right-1"
                        onClick={() => {
                          setSearchText('')
                          setShow(false)
                        }}
                      />
                    ) : (
                      ''
                    )}
                  </label>
                  <div className={'relative md:flex md:items-center m-auto px-1 mt-1'}>
                    <span
                      className={
                        selectedSpeakers && selectedSpeakers?.length < 5
                          ? 'text-sm font-normal leading-7 text-custom-gray-80 regular-font '
                          : 'text-sm font-normal leading-7 text-[#eab308] regular-font '
                      }
                    >
                      {error}
                    </span>

                    {speakersDetails?.length === 0 &&
                      speakersDetails[0] !== 'Loading...' &&
                      searchText.length >= 2 &&
                      !isLoading && (
                        <span className="text-lg font-normal leading-7 text-[#808080] regular-font">
                          No speakers match your search criteria
                        </span>
                      )}
                    {speakersDetails[0] == 'Loading...' && searchText.length >= 2 && !isLoading && (
                      <span className="text-lg font-normal leading-7 text-[#808080] regular-font">
                        {speakersDetails && speakersDetails}
                      </span>
                    )}
                  </div>
                  <div className="relative -mt-1">
                    {show && (
                      <div
                        className={
                          searchText.length >= 2
                            ? 'absolute z-10 w-full text-white  bg-black border border-custom-gray-80'
                            : ''
                        }
                      >
                        {isSearchContainerLoading ? (
                          <Loader isNavBar={true} />
                        ) : (
                          <div className="">
                            {suggestions?.nameList &&
                              suggestions?.nameList?.slice(0, 5).map((item, i) => (
                                <Fragment key={i}>
                                  {item && (
                                    <div className="z-0" onClick={() => onSpeakerSelected(item)}>
                                      <div className="w-full px-2 leading-loose bg-black regular-font hover:bg-opacity-20 hover:bg-gray-200">
                                        <div className="flex my-2 cursor-pointer">
                                          {item?.image_url !== undefined ? (
                                            <Image
                                              height={30}
                                              width={50}
                                              objectFit="cover"
                                              objectPosition="top"
                                              src={
                                                'https:' +
                                                item?.image_url +
                                                '?w=50&h=50&q=100&fit=thumb&f=top'
                                              }
                                              alt=""
                                            />
                                          ) : (
                                            <Image
                                              height={30}
                                              width={50}
                                              objectFit="cover"
                                              objectPosition="top"
                                              src={
                                                'https://images.ctfassets.net/p6iqoqavudao/3lLPw0ct3NWRdllJaozgZr/238aa02d0b3a99038d1969146d7916f1/placeholder.png'
                                              }
                                              alt=""
                                            />
                                          )}

                                          <Highlighter
                                            className="mt-3 ml-4 text-md text-custom-gray-80 search-font"
                                            highlightClassName="relative text-white bg-inherit font-normal text-md"
                                            searchWords={[searchText]}
                                            autoEscape={true}
                                            textToHighlight={item?.name}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Fragment>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="py-2 text-xs text-left text-red-500">
                    {selectedSpeakers && selectedSpeakers?.length > 0
                      ? ''
                      : errors.searchText?.message}
                  </p>
                </div>
                <div></div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-8 xs:grid-cols-2">
              <div className="mt-6 ">
                <div className="flex bg-black border-[1.5px] border-gray-500 md:mt-10 xs:mt-6 mt-4">
                  <Link href={speakerData && `/speaker/${speakerData[0]?.slug}`} legacyBehavior>
                    {speakerData?.[0]?.assets?.fields.file?.url !== undefined ? (
                      <Image
                        src={
                          speakerData &&
                          `https:${speakerData?.[0]?.assets?.fields.file?.url}?w=72&h=73&q=100&fit=thumb&f=top`
                        }
                        alt=""
                        width={72}
                        height={73}
                        quality={100}
                      />
                    ) : (
                      <Image
                        src={`${defaultImageUrl}?w=72&h=73&q=100&fit=thumb&f=top`}
                        alt=""
                        width={72}
                        height={73}
                        quality={100}
                      />
                    )}
                  </Link>
                  <div className="w-full leading-normal md:p-4">
                    <div className="flex">
                      <div className="py-1 ml-2 cursor-pointer text-md maxMd:mt-4">
                        <Link
                          href={speakerData && `/speaker/${speakerData[0]?.slug}`}
                          legacyBehavior
                        >
                          <a>{speakerData && speakerData?.[0]?.name}</a>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6">
            {!loggedInUserDetails?.userDetails && (
              <h1 className="pt-10 pb-5 text-lg tracking-wider uppercase tagline-font lg:text-2xl md:text-2xl maxSm:text-sm xs:text-md">
                contact information
              </h1>
            )}
            <div className="flex flex-col gap-8 ">
              <form onSubmit={handleSubmit(onSubmit)} noValidate autocomplete="off">
                {!loggedInUserDetails?.userDetails && (
                  <div className="grid grid-cols-1 gap-8 xs:grid-cols-2">
                    <div className="mt-6">
                      <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                          <div className="relative z-0 group">
                            <input
                              {...field}
                              type="text"
                              name="firstName"
                              id="firstName"
                              className="block py-2.5 px-0 w-full border-b border-b-custom-gray-80 border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer rounded-none"
                              placeholder=" "
                            />
                            <label
                              htmlFor="firstName"
                              className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                            >
                              First Name{asterisk}
                            </label>
                          </div>
                        )}
                      />

                      <p className="py-2 text-xs text-left text-red-500">
                        {errors.firstName?.message}
                      </p>
                    </div>
                    <div className="mt-6">
                      <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                          <div className="relative z-0 group">
                            <input
                              {...field}
                              type="text"
                              name="lastName"
                              id="lastName"
                              className="block py-2.5 px-0 w-full border-b border-b-custom-gray-80 border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer rounded-none"
                              placeholder=" "
                            />
                            <label
                              htmlFor="lastName"
                              className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                            >
                              Last Name{asterisk}
                            </label>
                          </div>
                        )}
                      />
                      <p className="py-2 text-xs text-left text-red-500">
                        {errors.lastName?.message}
                      </p>
                    </div>
                    <div className="mt-6">
                      <div className="flex gap-4">
                        <div className="w-2/12 mt-[-28px]">
                          <label className="text-xs text-custom-gray-80 inline-block">
                            Country
                          </label>
                          <Dropdown
                            value={countryCode}
                            onInputChange={(e) => {
                              setCountryCode(e)
                              setCountryDialCode(
                                countryCodes.filter((ele) => ele.code == e)[0].dial_code
                              )
                            }}
                            options={countryCodes}
                          ></Dropdown>
                        </div>
                        <div className="w-10/12 mt-[4px]">
                          <Controller
                            name="mobilePhone"
                            control={control}
                            render={({ field }) => (
                              <div className="relative z-0 group">
                                <input
                                  {...field}
                                  type="text"
                                  name="mobilePhone"
                                  id="mobilePhone"
                                  className="block w-full px-0 py-2 text-white bg-transparent border-0 border-b rounded-none appearance-none border-b-custom-gray-80 dark:text-white focus:outline-none peer"
                                  placeholder=" "
                                />
                                <label
                                  htmlFor="mobilePhone"
                                  className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                >
                                  Phone Number{asterisk}
                                </label>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                      <p className="relative bottom-6 maxSm:bottom-2 text-xs text-left text-red-500">
                        {errors.mobilePhone?.message}
                      </p>
                    </div>
                    <div className="mt-6">
                      <Controller
                        name="emailAddress"
                        control={control}
                        render={({ field }) => (
                          <div className="relative z-0 group">
                            <input
                              {...field}
                              type="text"
                              name="emailAddress"
                              id="emailAddress"
                              className="block py-2.5 px-0 w-full border-b border-b-custom-gray-80 border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer  rounded-none"
                              placeholder=" "
                            />
                            <label
                              htmlFor="emailAddress"
                              className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                            >
                              Email Address{asterisk}
                            </label>
                          </div>
                        )}
                      />
                      <p className="py-2 text-xs text-left text-red-500">
                        {errors.emailAddress?.message}
                      </p>
                    </div>
                    <div className="">
                      <Controller
                        name="company"
                        control={control}
                        render={({ field }) => (
                          <div className="relative z-0 group">
                            <input
                              {...field}
                              type="text"
                              name="company"
                              id="company"
                              className="block py-2.5 px-0 w-full border-b border-b-custom-gray-80 border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer rounded-none"
                              placeholder=" "
                            />
                            <label
                              htmlFor="company"
                              className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                            >
                              Company{asterisk}
                            </label>
                          </div>
                        )}
                      />
                      <p className="py-2 text-xs text-left text-red-500">
                        {errors.company?.message}
                      </p>
                    </div>
                    <div className="">
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <div className="relative z-0 group">
                            <input
                              {...field}
                              type="text"
                              name="title"
                              id="title"
                              className="block py-2.5 px-0 w-full border-b border-b-custom-gray-80 border-0 text-white dark:text-white appearance-none bg-transparent focus:outline-none peer rounded-none"
                              placeholder=" "
                            />
                            <label
                              htmlFor="title"
                              className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                            >
                              Job Title
                            </label>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
                <h1 className="py-10 text-base text-lg font-bold uppercase tagline-font lg:text-2xl md:text-2xl maxSm:text-sm xs:text-md">
                  Event Information
                </h1>
                <div className="grid grid-cols-1 gap-8 xs:grid-cols-2">
                  <div>
                    <label className="text-xs text-custom-gray-80">Budget{asterisk}</label>

                    <Controller
                      name="budgetPerEvent"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Popover className="relative">
                          {({ open }) => (
                            <>
                              <Popover.Button className="inline-flex items-center justify-between w-full px-2 py-2 text-left border-b border-b-custom-gray-80 focus:outline-none">
                                <span>
                                  {getValues('budgetPerEvent') ? getValues('budgetPerEvent') : '--'}
                                </span>
                                <BsChevronDown
                                  className={
                                    'text-custom-gray-80 mr-2 transition duration-150 ease-in-out group-hover:text-opacity-80'
                                  }
                                  aria-hidden="true"
                                />
                              </Popover.Button>
                              <Popover.Panel className="absolute z-10 w-full text-white -translate-y-20 bg-black border border-custom-gray-80">
                                {({ close }) => (
                                  <div className="flex flex-col gap-2">
                                    <p className="px-4 mt-4 text-xs text-custom-gray-80">Budget</p>
                                    <div className="flex items-center justify-between px-4">
                                      <p>--</p>
                                      <p className="text-custom-gray-80">
                                        <BsChevronDown
                                          className={
                                            open ? 'rotate-180 transform cursor-pointer' : ''
                                          }
                                          onClick={() => close()}
                                        />
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1 text-sm font-normal py-1.5">
                                      {budget.map((item, i) => (
                                        <div
                                          key={i}
                                          className={classNames(
                                            'py-2 hover:bg-custom-gray-500',
                                            getValues('budgetPerEvent') &&
                                              getValues('budgetPerEvent') == item
                                              ? 'bg-custom-gray-500'
                                              : ''
                                          )}
                                        >
                                          <p
                                            className="px-4 tracking-wide cursor-default regular-font hover:cursor-pointer"
                                            onClick={() => {
                                              onChange(item), close()
                                            }}
                                          >
                                            {item}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </Popover.Panel>
                            </>
                          )}
                        </Popover>
                      )}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.budgetPerEvent?.message}
                    </p>
                  </div>

                  <div>
                    <Calendar
                      fieldName={'eventDate'}
                      getValues={getValues}
                      setValue={setValue}
                      clearErrors={clearErrors}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.eventDate?.message || error?.eventDate}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-custom-gray-80">Event Type</label>
                    <Controller
                      name="eventType"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Popover className="relative">
                          {({ open }) => (
                            <>
                              <Popover.Button className="inline-flex items-center justify-between w-full px-2 py-2 text-left border-b border-b-custom-gray-80 focus:outline-none">
                                {getValues('eventType') ? getValues('eventType') : '--'}

                                <BsChevronDown
                                  className={
                                    'text-custom-gray-80 mr-2 transition duration-150 ease-in-out group-hover:text-opacity-80'
                                  }
                                  aria-hidden="true"
                                />
                              </Popover.Button>
                              <Popover.Panel className="absolute z-10 w-full text-white -translate-y-20 bg-black border border-custom-gray-80">
                                {({ close }) => (
                                  <div className="flex flex-col gap-2">
                                    <p className="px-4 mt-4 text-xs text-custom-gray-80">
                                      Event Type
                                    </p>
                                    <div className="flex items-center justify-between px-4">
                                      <p>--</p>
                                      <p className="text-custom-gray-80">
                                        <BsChevronDown
                                          className={
                                            open ? 'rotate-180 transform cursor-pointer' : ''
                                          }
                                          onClick={() => close()}
                                        />
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1 text-sm font-normal py-1.5">
                                      {eventTypes.map((item, i) => (
                                        <div
                                          key={i}
                                          className={classNames(
                                            'py-2 hover:bg-custom-gray-500',
                                            getValues('eventType') && getValues('eventType') == item
                                              ? 'bg-custom-gray-500'
                                              : ''
                                          )}
                                        >
                                          <p
                                            className="px-4 tracking-wide cursor-default regular-font hover:cursor-pointer"
                                            onClick={() => {
                                              onChange(item), close()
                                            }}
                                          >
                                            {item}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </Popover.Panel>
                            </>
                          )}
                        </Popover>
                      )}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.eventType?.message}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-custom-gray-80">Event Country{asterisk}</label>
                    <Controller
                      name="eventCountry"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Popover className="relative">
                          {({ open }) => (
                            <>
                              <Popover.Button className="inline-flex items-center justify-between w-full px-2 py-2 text-left border-b border-b-custom-gray-80">
                                <span>
                                  {getValues('eventCountry') ? getValues('eventCountry') : '--'}
                                </span>
                                <BsChevronDown
                                  className={
                                    'text-custom-gray-80 mr-2 transition duration-150 ease-in-out group-hover:text-opacity-80'
                                  }
                                  aria-hidden="true"
                                />
                              </Popover.Button>
                              <Popover.Panel className="absolute z-10 w-full text-white -translate-y-20 bg-black border border-custom-gray-80">
                                {({ close }) => (
                                  <div className="flex flex-col gap-2">
                                    <p className="px-4 mt-4 text-xs text-custom-gray-80">
                                      Event Country
                                    </p>
                                    <div className="flex items-center justify-between px-4">
                                      <p>--</p>
                                      <p className="text-custom-gray-80">
                                        <BsChevronDown
                                          className={
                                            open ? 'rotate-180 transform cursor-pointer' : ''
                                          }
                                          onClick={() => close()}
                                        />
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1 text-sm font-normal py-1.5 max-h-40 overflow-auto">
                                      {countries.map((item, i) => (
                                        <div
                                          key={i}
                                          className={classNames(
                                            'py-2 hover:bg-custom-gray-500',
                                            getValues('eventCountry') &&
                                              getValues('eventCountry') == item.value
                                              ? 'bg-custom-gray-500'
                                              : ''
                                          )}
                                        >
                                          <p
                                            className="px-4 tracking-wide cursor-pointer regular-font"
                                            onClick={() => {
                                              onChange(item.value), close()
                                            }}
                                          >
                                            {item.label}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </Popover.Panel>
                            </>
                          )}
                        </Popover>
                      )}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.eventCountry?.message}
                    </p>
                  </div>

                  <div className="mt-6">
                    <Controller
                      name="attendees"
                      control={control}
                      render={({ field }) => (
                        <div className="relative z-0 group">
                          <input
                            {...field}
                            type="text"
                            name="attendees"
                            id="attendees"
                            className="block w-full px-0 py-2 text-white bg-transparent border-0 border-b rounded-none appearance-none border-b-custom-gray-80 dark:text-white focus:outline-none peer"
                            placeholder=" "
                          />
                          <label
                            htmlFor="attendees"
                            className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                          >
                            Number Of Attendees{asterisk}
                          </label>
                        </div>
                      )}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.attendees?.message}
                    </p>
                  </div>
                  <div className="mt-6">
                    <Controller
                      name="eventLocation"
                      control={control}
                      render={({ field }) => (
                        <div className="relative z-0 group">
                          <input
                            {...field}
                            type="text"
                            name="eventLocation"
                            id="eventLocation"
                            className="block w-full px-0 py-2 text-white bg-transparent border-0 border-b rounded-none appearance-none border-b-custom-gray-80 dark:text-white focus:outline-none peer"
                            placeholder=" "
                          />
                          <label
                            htmlFor="eventLocation"
                            className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                          >
                            Event Location{asterisk}
                          </label>
                        </div>
                      )}
                    />
                    <p className="py-2 text-xs text-left text-red-500">
                      {errors.eventLocation?.message}
                    </p>
                  </div>
                </div>
                <div className="mt-14">
                  <Controller
                    name="eventDetails"
                    control={control}
                    render={({ field }) => (
                      <div className="relative z-0 group">
                        <textarea
                          {...field}
                          type="text"
                          name="eventDetails"
                          id="eventDetails"
                          className="relative block w-full px-0 py-2 text-white bg-transparent border-0 border-b rounded-none appearance-none top-2 border-b-custom-gray-80 dark:text-white focus:outline-none peer"
                          placeholder=" "
                        ></textarea>
                        <label
                          htmlFor="eventDetails"
                          className="peer-focus:font-medium absolute text-sm text-custom-gray-80 dark:text-custom-gray-80 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:dark:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                        >
                          Event Details{asterisk}
                        </label>
                      </div>
                    )}
                  />
                  <p className="py-2 mt-2 text-xs text-left text-red-500">
                    {errors.eventDetails?.message}
                  </p>
                </div>
                <div className="mt-12 mb-24">
                  <Button
                    className="border px-12 py-3 text-xs lg:text-lg font-bold tagline-font tracking-wider w-full xs:w-2/5 md:!w-72 button-hover"
                    label={booknow ? 'Submit Request' : 'Submit'}
                    type="submit"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingRequestForm

const budget = [
  'Under $5,000',
  '$5,000 - $10,000',
  '$10,000 - $20,000',
  '$20,000 - $30,000',
  '$30,000 - $50,000',
  '$50,000 - $75,000',
  '$75,000 - $100,000',
  '$100,000+',
]

const eventTypes = [
  'Virtual',
  'Book Signing',
  'Keynote Speech',
  'Conference',
  'Evening Show',
  'Fireside Chat with Q&A',
  'Workshop',
  'Flexible',
  'Other',
]
